import { Avatar } from '@/components/ui';
import type { ProjectView } from '@/types/current-project';
import type { ProjectBrief } from '@/api/adapter/projectBrief';
import type { ProjectParticipant } from '@/api/adapter/projectParticipants';
import { profileImageUrl } from '@/hooks/useProfile';

export function ProjectDetailsCard({
  project,
  brief,
  participants,
}: {
  project: ProjectView;
  brief?: ProjectBrief;
  participants?: { client: ProjectParticipant } | null;
}) {
  const client = participants?.client;
  const clientName =
    client?.profiles?.client?.name ?? client?.displayName ?? 'Client';
  const clientAvatarUrl =
    client?.clientImage && client.profiles
      ? profileImageUrl(client.profiles.principalId, 'client')
      : undefined;
  return (
    <div className="rounded-[--radi-6] border border-[--base-border] p-[--spacing-10] pb-[--spacing-12] shadow-elevation-xs">
      <div className="flex flex-col gap-[--spacing-12] text-left">
        {/* Title Block (vertical gap: 4px) */}
        <div className="flex flex-col gap-[--spacing-3]">
          <h2 className="text-3xl font-semibold text-[--text-dark-primary]">
            {project.title}
          </h2>
          {brief?.summary && (
            <p className="text-base font-normal text-[--text-dark-secondary]">
              {brief?.summary}
            </p>
          )}
        </div>

        {/* Owner Block (Frame 55) */}
        {client && (
          <div className="flex items-center gap-3">
            <Avatar src={clientAvatarUrl} alt={clientName} size="lg" />
            <div className="flex flex-col">
              <span className="text-base font-medium text-white">
                {clientName}
              </span>
              {project.createdAt != null && (
                <span className="text-sm font-normal text-[--text-dark-secondary]">
                  {new Date(project.createdAt * 1000).toLocaleDateString(
                    'en-US',
                    {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }
                  )}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Divider line */}
        <div className="border-b border-[--base-border]" />

        {/* Description Block */}
        <div className="projectScopeInfoBlock">
          <h3 className="title">Project Description</h3>
          <p className="description">
            {project.description || 'No description provided.'}
          </p>
        </div>

        {/* Objectives Block */}
        {brief?.objectives && brief?.objectives.length > 0 && (
          <div className="projectScopeInfoBlock">
            <h3 className="title">Key Objectives</h3>
            <ul className="description list-inside list-decimal space-y-1">
              {brief?.objectives.map((obj, i) => (
                <li key={i} className="pl-1">
                  {obj}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
